import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";

import BG_URL from "../img/SG.png";
import LOGO_URL from "../img/tmis_logo_orig.png";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_RESET = `${API_BASE}/setNewPassword`;

export default function ResetPassword() {
  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const e = params.get("e") || "";
    const c = params.get("c") || "";

    setEmail(e);
    setEmailCode(c);

    if (!e || !c) {
      setError("The reset link is invalid. Please use the link from your email.");
    }
  }, []);

  const validate = () => {
    if (!email || !emailCode) {
      return "The reset link is invalid or missing. Please use the link from your email.";
    }
    if (!password || !password2) {
      return "Please enter and confirm your new password.";
    }
    if (password.length < 6) {
      return "Password should be at least 8 characters.";
    }
    if (password !== password2) {
      return "Passwords do not match. Please check again.";
    }
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.post(
        ENDPOINT_RESET,
        {
          email: email.trim(),
          email_code: emailCode,
          password,
        },
        { timeout: 20000 }
      );

      if (!data?.success) {
        setError(data?.message || "Unable to reset password. Please try again.");
        return;
      }

      setSuccessMsg(data.message || "Your password has been reset. Please log in.");
      setPassword("");
      setPassword2("");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Server error. Please try again later.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="login-wrap"
      style={{
        minHeight: "100vh",
        width: "100%",
        backgroundImage: `url(${BG_URL})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        paddingTop: "70px",
        paddingBottom: "40px",
        backgroundAttachment: "fixed",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to bottom, rgba(255, 255, 255, 0) 70%, rgba(255, 255, 255, 1) 100%)",
        }}
      />

      <style>{`
        .register-card {
          width: 620px;
          max-width: 100%;
          background:
            linear-gradient( to bottom right, rgba(255,255,255,0.55), rgba(255,255,255,0.35) )
            border-box;
          backdrop-filter: blur(12px) saturate(120%);
          -webkit-backdrop-filter: blur(12px) saturate(120%);
          border: 1px solid rgba(255,255,255,0.45);
          border-radius: 22px;
          box-shadow: 0 12px 40px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.18);
        }
        .register-card::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: 22px;
          pointer-events: none;
          background: linear-gradient(180deg, rgba(255,255,255,0.35), rgba(255,255,255,0));
          mix-blend-mode: screen;
        }
        .text-holder {
          border-radius: 10px;
        }
        .btn-register {
          padding: 12px 16px;
          font-weight: 700;
          letter-spacing: .6px;
          border-radius: 12px;
          box-shadow: 0 6px 20px rgba(0,0,0,0.18);
        }
        @media (prefers-reduced-transparency: reduce) {
          .register-card {
            background: rgba(255,255,255,0.92);
            backdrop-filter: none;
          }
        }
        .back-link {
          font-size: 0.9rem;
          text-decoration: none;
        }
        .back-link:hover {
          text-decoration: underline;
        }
      `}</style>

      <Container style={{ zIndex: 1 }}>
        <Row className="justify-content-center text-center">
          <Col xs={12}>
            <img
              src={LOGO_URL}
              alt="TMIS Logo"
              style={{
                height: 90,
                objectFit: "contain",
                marginBottom: 18,
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            />
          </Col>

          <Col xs={12} sm={10} md={8} lg={6} xl={5}>
            <Card className="register-card position-relative mx-auto custom-font">
              <Card.Body style={{ padding: 24 }}>
                <h3
                  className="fw-bold text-center"
                  style={{ letterSpacing: 1.1, marginBottom: 22, fontSize: 24 }}
                >
                  Reset Password
                </h3>

                <p className="text-center mb-4">
                  Please set a new password for your TMIS Learn + Do account.
                </p>

                {error ? (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                ) : null}

                {successMsg ? (
                  <Alert variant="success" className="mb-3">
                    {successMsg}
                  </Alert>
                ) : null}

                <Form onSubmit={onSubmit} style={{ textAlign: "left" }}>
                  <Form.Group className="mb-3" controlId="resetEmail">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      size="lg"
                      type="email"
                      value={email}
                      readOnly
                      className="text-holder"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="newPassword">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                      size="lg"
                      type="password"
                      placeholder="Enter new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="text-holder"
                      autoComplete="new-password"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="confirmPassword">
                    <Form.Label>Confirm New Password</Form.Label>
                    <Form.Control
                      size="lg"
                      type="password"
                      placeholder="Re-enter new password"
                      value={password2}
                      onChange={(e) => setPassword2(e.target.value)}
                      className="text-holder"
                      autoComplete="new-password"
                    />
                  </Form.Group>

                  <div className="d-grid">
                    <Button
                      className="btn-register custom-font"
                      type="submit"
                      disabled={submitting || !email || !emailCode}
                    >
                      {submitting ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Updating password…
                        </>
                      ) : (
                        "RESET PASSWORD"
                      )}
                    </Button>
                  </div>
                </Form>

                <div className="mt-3 d-flex justify-content-between align-items-center">
                  <a href="/" className="back-link">
                    Back to login
                  </a>
                  <a href="/forgotpassword" className="back-link">
                    Request a new link
                  </a>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
